import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { dialog, toast, i18N } from '@/common/util'
import secaoService from '@/services/secao.service'
import { useSecoesStore } from './index'

import type { OrderSectionsType } from '@/types/types'

export const useSecoesAdminStore = defineStore('secoesAdmin', () => {
  const secoesStore = useSecoesStore()

  /**
   * States
   */
  const list = ref<any[]>([])
  const status = ref(0)

  const loading = computed(() => status.value === 1)

  const getAll = async () => {
    status.value = 1
    try {
      const { data } = await secaoService.get<any[]>()
      list.value = data
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  const create = async (dataObj: any, file: string) => {
    status.value = 1

    try {
      const { success, data } = await secaoService.post<{ id: string }>(null, dataObj)
      const result = data

      if (file) await secaoService.fileUpload(result.id, file)

      if (success) {
        toast.success({ message: i18N('toasts.createSuccess') })
        secoesStore?.emptySectionsPool()
        getAll()
      }

      return data as any
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  const remove = async (secao: any, force?: boolean) => {

    if (!force)
      return dialog.open({
        title: i18N('feedback.attention'),
        message: i18N('dialogs.delete.message', {type: i18N('labels.section').toLowerCase(), name: secao.nome}),
        complete: () => remove(secao, true)
      })

    try {
      status.value = 1

      const { success } = await secaoService.delete(secao.id)

      if (success) {
        toast.open({type: 'success', message: i18N('toasts.removeSuccess')})
        list.value.splice(list.value.findIndex(el => el.id === secao.id), 1)
        secoesStore?.emptySectionsPool()
      }

      status.value = 2
      return success
    } catch {
      status.value = -1
    }
  }

  const update = async (dataObj: any, id: string, file: string) => {
    status.value = 1

    try {
      const { data, success } = await secaoService.put(id, dataObj)
      if (success && file) await secaoService.fileUpload(id, file)
      if (success) {
        const value = list.value.find(el => el.id === data.id)
        value && Object.assign(value, data)
        toast.success({message: i18N('toasts.updateSuccess')})
        secoesStore?.emptySectionsPool()
      }
      status.value = 2
      return data
    } catch {
      status.value = -1
    }
  }

  const associateSections = async (idsSecoes: string[]) => {
    status.value = 1

    try {
      //@TODO: Implementar lógica quando endpoint for construido
      return idsSecoes
    } catch {
      //
    } finally {
      status.value = 2
    }
  }

  const getById = async (id: string): Promise<any> => {
    status.value = 1
    try {
      const { data } = await secaoService.get(id)
      status.value = 2
      return data
    } catch {
      status.value = -1
    }
  }

  const saveOrderSections = async (dataObj: OrderSectionsType): Promise<void> => {
    status.value = 1
    try {
      const { success } = await secaoService.sendOrderSections(dataObj)

      if (success) {
        toast.open({type: 'success', message: i18N('toasts.sections.sortingSaved')})
        secoesStore?.emptySectionsPool()

        getAll()
      }
      status.value = 2
    } catch {
      status.value = -1
    }
  }

  const associationContracts = async (sectionId: string, contracstIds: string[]) => {
    status.value = 1
    try {
      const { success } = await secaoService.associationContracts(sectionId, contracstIds)

      if (success) toast.open({type: 'success', message: i18N('toasts.sections.contractLinked')})
      
      status.value = 2

      return success
    } catch {
      status.value = -1
    }
  }

  return {
    // State
    list,
    // Getters
    loading,
    // Actions
    saveOrderSections,
    getAll,
    create,
    update,
    remove,
    associateSections,
    getById,
    associationContracts,
  }
})

export default useSecoesAdminStore